import Axios from 'axios';
import {
  FETCH_AUTO_ACCEPT_REQUEST,
  FETCH_AUTO_ACCEPT_SUCCESS,
  FETCH_AUTO_ACCEPT_FAILURE,
  UPDATE_AUTO_ACCEPT_REQUEST,
  UPDATE_AUTO_ACCEPT_SUCCESS,
  UPDATE_AUTO_ACCEPT_FAILURE,
  CLEAR_AUTO_ACCEPT_ERRORS_REQUEST,
  CLEAR_AUTO_ACCEPT_ERRORS_SUCCESS,
  CLEAR_AUTO_ACCEPT_ERRORS_FAILURE,
  FETCH_AUTO_ACCEPT_STATUS_REQUEST,
  FETCH_AUTO_ACCEPT_STATUS_SUCCESS,
  FETCH_AUTO_ACCEPT_STATUS_FAILURE
} from '../constants/autoAccept';
import axios, { AxiosError } from 'axios';
import { sendError } from '@pec/aion-ui-core/sendError';
import { AutoAcceptStatusPayload, FetchAutoAcceptPayload, UpdateAutoAcceptParams } from '../interfaces/autoAccept';
import { ThunkAction } from 'redux-thunk';
import { RootState } from '../combineReducers';

const fetchAutoAcceptRequest = () =>
  ({
    type: FETCH_AUTO_ACCEPT_REQUEST
  } as const);

const fetchAutoAcceptSuccess = (payload: FetchAutoAcceptPayload) =>
  ({
    type: FETCH_AUTO_ACCEPT_SUCCESS,
    payload
  } as const);

const fetchAutoAcceptFailure = (error: AxiosError) => {
  sendError(error);
  return {
    type: FETCH_AUTO_ACCEPT_FAILURE,
    error
  } as const;
};

export const fetchAutoAccept = (
  organizationId: string
): ThunkAction<void, RootState, null, Actions> => async dispatch => {
  try {
    dispatch(fetchAutoAcceptRequest());

    const { data } = await axios.get<FetchAutoAcceptPayload>(
      `/api/v1/CompanyProfile/${organizationId}/AutomaticRelease`
    );

    dispatch(fetchAutoAcceptSuccess(data));
  } catch (error) {
    dispatch(fetchAutoAcceptFailure(error));
  }
};

const updateAutoAcceptRequest = () =>
  ({
    type: UPDATE_AUTO_ACCEPT_REQUEST
  } as const);

const updateAutoAcceptSuccess = (payload: FetchAutoAcceptPayload) =>
  ({
    type: UPDATE_AUTO_ACCEPT_SUCCESS,
    payload
  } as const);

const updateAutoAcceptFailure = (error: AxiosError) => {
  sendError(error);
  return {
    type: UPDATE_AUTO_ACCEPT_FAILURE,
    error
  } as const;
};

export const updateAutoAccept = ({
  organizationId,
  status,
  afterSuccess
}: UpdateAutoAcceptParams): ThunkAction<void, RootState, null, Actions> => async dispatch => {
  try {
    dispatch(updateAutoAcceptRequest());

    const { data } = await axios.put<FetchAutoAcceptPayload>(
      `/api/v1/CompanyProfile/${organizationId}/AutomaticRelease`,
      status
    );
    afterSuccess && afterSuccess();

    dispatch(updateAutoAcceptSuccess(data));
  } catch (error) {
    dispatch(updateAutoAcceptFailure(error));
  }
};

const fetchAutoAcceptStatusRequest = () =>
  ({
    type: FETCH_AUTO_ACCEPT_STATUS_REQUEST
  } as const);

const fetchAutoAcceptStatusSuccess = (payload: AutoAcceptStatusPayload) =>
  ({
    type: FETCH_AUTO_ACCEPT_STATUS_SUCCESS,
    payload
  } as const);

const fetchAutoAcceptStatusFailure = (error: AxiosError) => {
  sendError(error);
  return {
    type: FETCH_AUTO_ACCEPT_STATUS_FAILURE,
    error
  } as const;
};

export const fetchAutoAcceptStatus = (
  companyId: string
): ThunkAction<void, RootState, null, Actions> => async dispatch => {
  try {
    dispatch(fetchAutoAcceptStatusRequest());

    const { data } = await axios.get<AutoAcceptStatusPayload>(`/api/v1/billing/Company/${companyId}/autoAcceptStatus`);

    dispatch(fetchAutoAcceptStatusSuccess(data));
  } catch (error) {
    dispatch(fetchAutoAcceptStatusFailure(error));
  }
};

const clearAutoAcceptErrorsRequest = () =>
  ({
    type: CLEAR_AUTO_ACCEPT_ERRORS_REQUEST
  } as const);

const clearAutoAcceptErrorsSuccess = () =>
  ({
    type: CLEAR_AUTO_ACCEPT_ERRORS_SUCCESS
  } as const);

const clearAutoAcceptErrorsFailure = (error: AxiosError) => {
  sendError(error);
  return {
    type: CLEAR_AUTO_ACCEPT_ERRORS_FAILURE,
    error
  } as const;
};

export const clearAutoAcceptErrors = (
  companyId: number
): ThunkAction<void, RootState, null, Actions> => async dispatch => {
  try {
    dispatch(clearAutoAcceptErrorsRequest());

    await Axios.delete(`/api/v1/billing/Company/${companyId}/autoAcceptStatus`);

    dispatch(clearAutoAcceptErrorsSuccess());
  } catch (error) {
    dispatch(clearAutoAcceptErrorsFailure(error));
  }
};

export type Actions =
  | ReturnType<typeof fetchAutoAcceptRequest>
  | ReturnType<typeof fetchAutoAcceptSuccess>
  | ReturnType<typeof fetchAutoAcceptFailure>
  | ReturnType<typeof updateAutoAcceptRequest>
  | ReturnType<typeof updateAutoAcceptSuccess>
  | ReturnType<typeof updateAutoAcceptFailure>
  | ReturnType<typeof clearAutoAcceptErrorsRequest>
  | ReturnType<typeof clearAutoAcceptErrorsSuccess>
  | ReturnType<typeof clearAutoAcceptErrorsFailure>
  | ReturnType<typeof fetchAutoAcceptStatusRequest>
  | ReturnType<typeof fetchAutoAcceptStatusSuccess>
  | ReturnType<typeof fetchAutoAcceptStatusFailure>;
